import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  createAccessGroup,
  deleteAccessGroup,
  fetchAccessGroups,
  updateAccessGroup
} from '@/features/org/orgSlice'
import {
  selectAccessGroups,
  selectGroupsCache,
  selectGroupsError
} from '@/features/org/orgSelectors'
import type { AccessGroup } from '@/features/org/types'
import { useAppDispatch, useAppSelector } from '@/store'

const columnHeadClass =
  'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'
const inputClass = 'rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground'

interface DraftState {
  open: boolean
  /** The group id being edited, or null for create. */
  groupId: string | null
  name: string
  description: string
  permissionsText: string
}

const CLOSED: DraftState = {
  open: false,
  groupId: null,
  name: '',
  description: '',
  permissionsText: ''
}

/** Permissions as chips-in / chips-out (comma-separated wire array). */
const toPermissions = (text: string): string[] =>
  text
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '')

/**
 * The access groups panel (PX-W6.2, AC-4): table + inline create/edit row +
 * delete with confirm. `memberCount` renders server-derived, never editable.
 */
export function AccessGroupsPanel() {
  const { t } = useTranslation('orgSettings')
  const dispatch = useAppDispatch()

  const groups = useAppSelector(selectAccessGroups)
  const cache = useAppSelector(selectGroupsCache)
  const error = useAppSelector(selectGroupsError)

  const [draft, setDraft] = useState<DraftState>(CLOSED)

  useEffect(() => {
    void dispatch(fetchAccessGroups())
  }, [dispatch])

  const onNewClicked = (): void => setDraft({ ...CLOSED, open: true })

  const onEditClicked = (group: AccessGroup): void => {
    setDraft({
      open: true,
      groupId: group.id ?? group.slug ?? group.name,
      name: group.name,
      description: group.description ?? '',
      permissionsText: (group.permissions ?? []).join(', ')
    })
  }

  const onDeleteClicked = (group: AccessGroup): void => {
    if (!window.confirm(t('groups.deleteConfirm', { name: group.name }))) return
    const id = group.id ?? group.slug ?? group.name
    void dispatch(deleteAccessGroup(id))
  }

  const onSaveClicked = (): void => {
    if (draft.name.trim() === '') return
    const input = {
      name: draft.name.trim(),
      description: draft.description.trim() === '' ? undefined : draft.description.trim(),
      permissions: toPermissions(draft.permissionsText)
    }
    if (draft.groupId === null) {
      void dispatch(createAccessGroup(input))
    } else {
      void dispatch(updateAccessGroup({ groupId: draft.groupId, input }))
    }
    setDraft(CLOSED)
  }

  return (
    <section
      aria-labelledby="access-groups-label"
      className="rounded-lg border border-border bg-card px-4 py-4"
    >
      <div id="access-groups-label" className="flex flex-wrap items-center justify-between gap-2">
        <SectionLabel>{t('groups.title')}</SectionLabel>
        <button
          type="button"
          onClick={onNewClicked}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t('groups.new')}
        </button>
      </div>
      <div className="mt-3">
        {cache.loading ? (
          <LoadingPanel />
        ) : error !== null && error.status === 403 ? (
          <ForbiddenPanel requested="/{orgId}/settings/access-groups" />
        ) : error !== null ? (
          <ErrorPanel error={error} onRetryClicked={() => void dispatch(fetchAccessGroups())} />
        ) : groups.length === 0 && !draft.open ? (
          <EmptyPanel body={t('groups.empty')} />
        ) : (
          <>
            {draft.open && (
              <div className="mb-4 rounded-lg border border-border bg-muted/40 px-3 py-3">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <label className="flex flex-col gap-1 text-sm text-ink-soft">
                    {t('groups.name')}
                    <input
                      type="text"
                      value={draft.name}
                      onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                      className={inputClass}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-ink-soft">
                    {t('groups.description')}
                    <input
                      type="text"
                      value={draft.description}
                      onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                      className={inputClass}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-ink-soft">
                    {t('groups.permissions')}
                    <input
                      type="text"
                      value={draft.permissionsText}
                      onChange={(event) =>
                        setDraft({ ...draft, permissionsText: event.target.value })
                      }
                      placeholder={t("groups.permissionsPlaceholder")}
                      className={inputClass}
                      aria-describedby="group-permissions-hint"
                    />
                    <span id="group-permissions-hint" className="text-xs text-muted-foreground">
                      {t('groups.permissionsHint')}
                    </span>
                  </label>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={onSaveClicked}
                    className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                  >
                    {t('groups.save')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDraft(CLOSED)}
                    className="rounded-md border border-input px-3 py-1.5 text-sm text-ink-soft hover:bg-muted"
                  >
                    {t('groups.cancel')}
                  </button>
                </div>
              </div>
            )}
            {groups.length > 0 && (
              <Table>
                <TableCaption className="sr-only">{t('groups.title')}</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead className={columnHeadClass}>{t('groups.name')}</TableHead>
                    <TableHead className={columnHeadClass}>{t('groups.description')}</TableHead>
                    <TableHead className={columnHeadClass}>{t('groups.permissions')}</TableHead>
                    <TableHead className={columnHeadClass}>{t('groups.memberCount')}</TableHead>
                    <TableHead className={columnHeadClass}> </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((group) => (
                    <TableRow key={group.id ?? group.slug ?? group.name}>
                      <TableCell className="py-2.5 font-medium text-foreground">
                        {group.name}
                      </TableCell>
                      <TableCell className="max-w-[18rem] text-sm text-ink-soft">
                        {group.description ?? '—'}
                      </TableCell>
                      <TableCell className="max-w-[20rem]">
                        <span className="flex flex-wrap gap-1">
                          {(group.permissions ?? []).map((permission) => (
                            <StatusPill key={permission} tone="neutral">
                              {permission}
                            </StatusPill>
                          ))}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm tabular-nums text-foreground">
                        {group.memberCount ?? '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => onEditClicked(group)}
                            className="rounded-md border border-input px-2 py-1 text-xs text-ink-soft hover:bg-muted"
                          >
                            {t('groups.edit')}
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteClicked(group)}
                            className="rounded-md border border-input px-2 py-1 text-xs text-blocked hover:bg-muted"
                          >
                            {t('groups.delete')}
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </div>
    </section>
  )
}
